import { useRefreshBlocker } from '@speaking-coach/ui'
import { useEffect, useId, useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  buttonVariants,
  Icon,
  Input,
  Notice,
} from '@speaking-coach/ui'
import { cn } from '../components/ui/cn'
import { ENROLLMENT_COLUMNS, ENROLLMENT_FILE_VERSION, MAX_ENROLLMENT_FILE_BYTES, enrollmentSample, parseEnrollmentFile, type EnrollmentFileRow } from './enrollment-file'

export function EnrollmentFileInput({ disabled, hasRows, onImport, onReadingChange }: {
  disabled: boolean
  hasRows: boolean
  onImport: (rows: EnrollmentFileRow[]) => void
  onReadingChange?: (reading: boolean) => void
}): JSX.Element {
  const [error, setError] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  const [replacement, setReplacement] = useState<EnrollmentFileRow[] | null>(null)
  useRefreshBlocker(reading || replacement !== null)
  const sampleLabelId = useId()
  const generation = useRef(0)
  useEffect(() => () => { generation.current++; onReadingChange?.(false) }, [onReadingChange])

  async function read(file: File): Promise<void> {
    if (disabled || reading) return
    setError(null)
    const format = file.name.toLowerCase().endsWith('.csv') ? 'csv' : file.name.toLowerCase().endsWith('.tsv') ? 'tsv' : null
    if (!format) { setError('Choose a CSV or TSV file.'); return }
    if (file.size > MAX_ENROLLMENT_FILE_BYTES) { setError('The file must be 1 MB or smaller. Split it and upload again.'); return }
    const current = ++generation.current
    setReading(true)
    onReadingChange?.(true)
    try {
      const buffer = await file.arrayBuffer()
      let text: string
      try { text = new TextDecoder('utf-8', { fatal: true }).decode(buffer) }
      catch { throw new Error('Save the spreadsheet as UTF-8 CSV or TSV and upload again.') }
      const result = parseEnrollmentFile(text, format)
      if (current !== generation.current) return
      if (hasRows) setReplacement(result.rows)
      else onImport(result.rows)
    } catch (failure) {
      if (current === generation.current) setError(failure instanceof Error ? failure.message : 'The file could not be read. Upload it again.')
    } finally { if (current === generation.current) { setReading(false); onReadingChange?.(false) } }
  }

  return (
    <section aria-label="Spreadsheet import" className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span id={sampleLabelId} className="inline-flex items-center gap-2 text-body text-text-secondary">
          <Icon name="download" />
          Download sample sheet
        </span>
        <div role="group" aria-labelledby={sampleLabelId} className="isolate inline-flex -space-x-px">
          {(['csv', 'tsv'] as const).map(format => (
            <a
              key={format}
              className={cn(
                buttonVariants({ variant: 'secondary', size: 'sm' }),
                'relative w-12 rounded-none first:rounded-l last:rounded-r focus-visible:z-10',
              )}
              aria-label={`Download sample ${format.toUpperCase()}`}
              download={`student-enrollment-v${ENROLLMENT_FILE_VERSION}.${format}`}
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(enrollmentSample(format))}`}
            >
              <span className="text-caption">{format.toUpperCase()}</span>
            </a>
          ))}
        </div>
      </div>
      <div className="rounded-token-card border border-border-default p-4" onDragOver={event => event.preventDefault()} onDrop={event => {
        event.preventDefault()
        if (event.dataTransfer.files.length !== 1) { setError('Upload one CSV or TSV file at a time.'); return }
        const file = event.dataTransfer.files[0]
        if (file) void read(file)
      }}>
        <label htmlFor="student-spreadsheet" className="block text-body font-medium mb-1">Student spreadsheet</label>
        <Input id="student-spreadsheet" type="file" accept=".csv,.tsv" disabled={disabled || reading} onChange={event => {
          const file = event.currentTarget.files?.[0]
          event.currentTarget.value = ''
          if (file) void read(file)
        }} />
        <p className="text-body text-text-secondary mt-2">Choose or drop a UTF-8 CSV/TSV file, up to 200 students and 1 MB. Replace the fictional sample row with your students.</p>
        <p className="text-caption text-text-secondary break-words mt-1">Required columns (samples use this order): <code>{ENROLLMENT_COLUMNS.join(',')}</code>. Keep the username column; its cells may be blank. Students enter or confirm their preferred name during onboarding. Columns are matched by header name. Keep existing headers and their values together; to reorder a sheet, move whole columns rather than rename headers.</p>
      </div>
      {reading && <p role="status">Reading spreadsheet…</p>}
      {error && <Notice variant="danger">{error}</Notice>}
      <AlertDialog open={replacement !== null} onOpenChange={open => { if (!open) setReplacement(null) }}>
        <AlertDialogContent>
          <AlertDialogTitle>Replace the current rows?</AlertDialogTitle>
          <AlertDialogDescription>The uploaded spreadsheet will replace all current rows and inline corrections.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep current rows</AlertDialogCancel>
            <AlertDialogAction disabled={disabled} onClick={() => { if (replacement && !disabled) onImport(replacement); setReplacement(null) }}>Replace rows</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
